import { FatalError, TransientError } from '../types/errors';
import { promisefy } from './promisefy';

export function obsSanityCheckFailed() {
  if (window.host?.removeVideoCompositionViewOverlay) {
    // Make sure video composition view overlay is not stuck on top of the browser
    window.host?.removeVideoCompositionViewOverlay?.(() => void 0);
  }

  const COOKIE_NAME = 'obs-api-sanity-check-failure-count';
  const MAX_FAILED_CHECKS = 3;
  const RETRY_INTERVAL_SECONDS = 5;

  const curr = parseInt(window.sessionStorage.getItem(COOKIE_NAME) || '0');
  const next = (isFinite(curr) ? curr : 0) + 1;

  if (next >= MAX_FAILED_CHECKS) {
    window.sessionStorage.removeItem(COOKIE_NAME);

    throw new FatalError('SE.Live API sanity check failed too many times. Please restart OBS.');
  }

  // This promise never resolves
  setTimeout(() => {
    window.sessionStorage.setItem(COOKIE_NAME, next.toString());

    location.reload();
  }, RETRY_INTERVAL_SECONDS * 1000);

  throw new TransientError(
    `Something is wrong with SE.Live API, I will try again in ${RETRY_INTERVAL_SECONDS} seconds.`
  );
}

let g_sanityCheckDone = false;

export async function ensureOBSPresence(doSanityCheck: boolean = true) {
  await new Promise<void>((resolve) => {
    if (window.host?.hostReady) {
      return resolve();
    }

    window.addEventListener('hostReady', () => setTimeout(resolve, 500), { once: true });
  });

  if (!doSanityCheck) {
    return;
  }

  if (g_sanityCheckDone) {
    return;
  }

  let success = true;

  const MIN_API_FUNCTIONS_COUNT = 140; // Latest SE.Live = 196

  if (Object.keys(window.host || {}).length < MIN_API_FUNCTIONS_COUNT) {
    success = false;
    
    console.warn('SE.Live sanity check failed: API functions count on window.host is less than expected');
  } else if (!(await promisefy(window.host.getAllAvailableAudioEncoderClasses)())?.length) {
    success = false;

    console.warn('SE.Live sanity check failed: window.host.getAllAvailableAudioEncoderClasses result is empty');
  } else if (!(await promisefy(window.host.getAllAvailableVideoEncoderClasses)())?.length) {
    success = false;

    console.warn('SE.Live sanity check failed: window.host.getAllAvailableVideoEncoderClasses result is empty');
  }

  if (!success) {
    obsSanityCheckFailed();
  } else {
    g_sanityCheckDone = true;
  }
}

export const minVersionForMultiStreaming = [25, 1, 1, 1];

function checkVersion(version: number[], minVersion: number[]): boolean {
  for (let i = 0; i < minVersion.length; i++) {
    const current = version[i] || 0;
    const minimum = minVersion[i] || 0;

    if (current > minimum) return true;
    if (current < minimum) return false;
    // if equal, continue checking next number
  }
  // if we get here, all numbers were equal
  return true;
}

export async function ensureOBSMinVersion({
  minVersion: minVersionProp = minVersionForMultiStreaming,
}: {
  minVersion?: typeof minVersionForMultiStreaming;
} = {}) {
  await ensureOBSPresence(false);

  if (!window.host.getHostProperties) {
    // sanityCheckFailed();

    //
    // Let the caller deal with missing SE.Live version information:
    //
    // We'll just assume it's the lowest possible version here and return that.
    //
    return {
      seLiveVersion: '0.0.0.0',
      seLiveMultiStreamingMinVersionOk: false,
      seLiveMultiStreamingMinVersion: minVersionProp.join('.'),
    };
  }

  const hostProps = await promisefy(window.host.getHostProperties)();
  const { hostPluginVersionString } = hostProps;

  if (!hostPluginVersionString) {
    //
    // Let the caller deal with missing SE.Live version information:
    //
    // We'll just assume it's the lowest possible version here and return that.
    //
    return {
      seLiveVersion: '0.0.0.0',
      seLiveMultiStreamingMinVersionOk: false,
      seLiveMultiStreamingMinVersion: minVersionProp.join('.'),
    };
  }

  const installedVersion = hostPluginVersionString.split('.').map((val) => Number(val)) as number[];

  return {
    seLiveVersion: hostPluginVersionString,
    seLiveMultiStreamingMinVersionOk: checkVersion(installedVersion, minVersionProp),
    seLiveMultiStreamingMinVersion: minVersionProp.join('.'),
  };
}
